// inventory-slider.ts — the inventory quantity-slider / move modal, carved out of template.js
// (roadmap T4.2, 2026-06-16). Byte-faithful port: the created DOM elements are typed `any` so the
// original body (numeric style assignments, dataset writes, on* handlers) ports verbatim; params are
// annotated `: any`. No other edits — behavior is identical. Self-contained popup create/destroy with
// no module-local state. removeInventorySliderOverlay is closure-bound inside the popup buttons, so it
// travels with the popup. Both are window-attached at the bottom for out-of-bundle consumers.

function showInventorySliderPopup(
	inventoryId: string,
	inventoryQty: number | string,
	movedFromInventoryId: string,
	dropzoneId: string,
	move_type: string,
	show_log_message: unknown,
	shouldRefresh: boolean,
	x_pos: unknown = null,
	z_pos: unknown = null,
	ft_moved: number | string = 0,
	casterCapacity: number | string = 0,
	grossWeight: number | string = 0,
	tradeQtyCallback: ((quantity: number) => void) | null = null
): void {
	const qty = Math.max(1, Number(inventoryQty) || 1);

	// Create the popup overlay
	const overlay = document.createElement('div');
	overlay.id = 'sliderOverlay';
	overlay.style.position = 'fixed';
	overlay.style.top = '0';
	overlay.style.left = '0';
	overlay.style.width = '100%';
	overlay.style.height = '100%';
	overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
	overlay.style.display = 'flex';
	overlay.style.justifyContent = 'center';
	overlay.style.alignItems = 'center';
	overlay.style.zIndex = '100000';

	// Create the popup container
	const popup = document.createElement('div');
	popup.id = 'sliderPopup';
	popup.style.backgroundColor = '#222';
	popup.style.padding = '20px';
	popup.style.borderRadius = '8px';
	popup.style.maxWidth = '90%';
	popup.style.width = '400px';
	popup.style.color = '#FFF';
	popup.style.position = 'relative';

	// Store casterCapacity and grossWeight as data attributes on popup for confirm check
	popup.dataset.show_log_message = String(show_log_message ?? '');
	popup.dataset.x_pos = JSON.stringify(x_pos); // could be null so we stringify
	popup.dataset.z_pos = JSON.stringify(z_pos);
	popup.dataset.ft_moved = String(ft_moved);
	popup.dataset.casterCapacity = String(casterCapacity);
	popup.dataset.grossWeight = String(grossWeight);

	// Cancel button
	const cancelButton = document.createElement('button');
	cancelButton.textContent = '×';
	cancelButton.style.position = 'absolute';
	cancelButton.style.top = '10px';
	cancelButton.style.right = '10px';
	cancelButton.style.border = 'none';
	cancelButton.style.backgroundColor = '#555';
	cancelButton.style.color = '#FFF';
	cancelButton.style.fontSize = '20px';
	cancelButton.style.cursor = 'pointer';
	cancelButton.style.width = '30px';
	cancelButton.style.height = '30px';
	cancelButton.style.borderRadius = '50%';
	cancelButton.style.display = 'flex';
	cancelButton.style.alignItems = 'center';
	cancelButton.style.justifyContent = 'center';
	cancelButton.onclick = async () => {
		removeInventorySliderOverlay();
		// Handle move_type-specific refreshes (if needed)
		if (move_type === 'drop_inventory') {
			// forceCasterArrReset();
		} else {
			// refreshCasterSheet();
		}
	};

	// Slider and input setup
	const label = document.createElement('p');
	label.textContent = `Move Quantity (1-${qty})`;
	const slider = document.createElement('input');
	slider.type = 'range';
	slider.min = '1';
	slider.max = String(qty);
	slider.value = String(qty);
	slider.style.width = '100%';
	const inputLeft = document.createElement('input');
	inputLeft.type = 'number';
	inputLeft.value = '0';
	inputLeft.min = '0';
	inputLeft.max = String(qty);
	inputLeft.style.width = '60px';
	inputLeft.style.textAlign = 'center';
	const inputRight = document.createElement('input');
	inputRight.type = 'number';
	inputRight.value = String(qty);
	inputRight.min = '1';
	inputRight.max = String(qty);
	inputRight.style.width = '60px';
	inputRight.style.textAlign = 'center';
	slider.oninput = () => {
		inputRight.value = slider.value;
		inputLeft.value = String(qty - Number(slider.value));
	};
	inputLeft.oninput = () => {
		inputRight.value = String(qty - Number(inputLeft.value));
		slider.value = inputRight.value;
	};
	inputRight.oninput = () => {
		inputLeft.value = String(qty - Number(inputRight.value));
		slider.value = inputRight.value;
	};

	// Confirm button
	const confirmButton = document.createElement('button');
	confirmButton.textContent = 'Confirm';
	confirmButton.style.marginTop = '20px';
	confirmButton.style.width = '100%';
	confirmButton.style.padding = '10px';
	confirmButton.style.backgroundColor = '#007bff';
	confirmButton.style.border = 'none';
	confirmButton.style.color = '#FFF';
	confirmButton.style.cursor = 'pointer';
	confirmButton.onclick = async () => {
		if (typeof tradeQtyCallback === 'function') {
			const q = parseInt(inputRight.value, 10) || 1;
			removeInventorySliderOverlay();
			tradeQtyCallback(q);
			if (shouldRefresh) await refreshSidePanel();
			return;
		}

		// Pull stored variables
		const casterCapacity = parseFloat(popup.dataset.casterCapacity ?? '0') || 0;
		const grossWeight = parseFloat(popup.dataset.grossWeight ?? '0') || 0;

		// Weight check before proceeding (if casterCapacity > 0)
		if (casterCapacity > 0) {
			const selectedQty = parseInt(inputRight.value);
			const selectedWeight = (selectedQty / qty) * grossWeight;
			if (selectedWeight > casterCapacity) {
				displayWarning("Selected quantity is too heavy to lift.");
				return; // Prevent move
			}
		}

		removeInventorySliderOverlay();

		await move_draggable_inventory(
			inventoryId, // inventory_id
			inputRight.value, // item_qty
			movedFromInventoryId, // move_from_inventory_id
			dropzoneId, // move_to_possess_id
			move_type, // move_type
			popup.dataset.show_log_message ?? '', // show_log_message
			JSON.parse(popup.dataset.x_pos ?? 'null'), // x_pos
			JSON.parse(popup.dataset.z_pos ?? 'null'), // z_pos
			parseInt(popup.dataset.ft_moved ?? '0') // ft_moved
		);
	};

	// Assemble the popup
	popup.appendChild(cancelButton);
	popup.appendChild(label);
	popup.appendChild(slider);
	const inputContainer = document.createElement('div');
	inputContainer.style.display = 'flex';
	inputContainer.style.justifyContent = 'space-between';
	inputContainer.style.marginTop = '10px';
	inputContainer.appendChild(inputLeft);
	inputContainer.appendChild(inputRight);
	popup.appendChild(inputContainer);
	popup.appendChild(confirmButton);
	overlay.appendChild(popup);
	document.body.appendChild(overlay);
}

function removeInventorySliderOverlay(overlayId: string = 'sliderOverlay'): void {
    const overlay = document.getElementById(overlayId);
    if (overlay) {
        document.body.removeChild(overlay);
    }
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. showInventorySliderPopup
// is called by the encounter-map DragManager (×3) + trade-trainer-init.js (×2), declared in
// encounter-map/globals.d.ts; removeInventorySliderOverlay is called by the ft3d-move-handlers bundle (and the
// popup's own confirm/cancel closures), declared in ft3d-move-handlers/globals.d.ts. Both reached as bare-
// identifier globals at runtime.
// ===========================================================================================
declare global {
	interface Window {
		showInventorySliderPopup: typeof showInventorySliderPopup;
		removeInventorySliderOverlay: typeof removeInventorySliderOverlay;
	}
}

Object.assign(window, {
	showInventorySliderPopup,
	removeInventorySliderOverlay,
});

export {};
